import type { ExperimentDetail } from '@studio/shared';
import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../shell/api.js';
import { subscribeToEvents } from '../shell/stream.js';
import { needsRefetch, versionsOf } from './reconcile.js';

export interface OpenExperiment {
  readonly detail: ExperimentDetail | null;
  readonly error: string | null;
  readonly loading: boolean;
  readonly refresh: () => void;
  readonly apply: (detail: ExperimentDetail) => void;
}

/**
 * One open Experiment, kept level with the server.
 *
 * The order is the one the shell already uses for background work: open the stream
 * first and buffer, then read the snapshot, then act only on what is newer. Reading
 * the snapshot first would lose every change that lands while it is in flight, which
 * is exactly the window a spread of six fills.
 */
export function useExperiment(experimentId: string): OpenExperiment {
  const [detail, setDetail] = useState<ExperimentDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const versions = useRef<ReadonlyMap<string, number>>(new Map());
  const [nonce, setNonce] = useState(0);

  const refresh = useCallback(() => setNonce((n) => n + 1), []);

  const apply = useCallback((next: ExperimentDetail) => {
    versions.current = versionsOf(next);
    setDetail(next);
  }, []);

  useEffect(() => {
    if (experimentId === '') return;
    let live = true;
    let snapshotApplied = false;
    const buffered: Parameters<Parameters<typeof subscribeToEvents>[0]>[0][] = [];
    // One read at a time, and one more afterwards if anything arrived while it ran.
    //
    // A spread publishes a dozen events in a moment, and reads fired in parallel come
    // back in whatever order the network gives them: the last to resolve wins, and if
    // that is an older snapshot the screen reverts to `Working…` for good - no event is
    // left to correct it, because the work has already finished.
    let inFlight = false;
    let again = false;

    const read = (): void => {
      if (inFlight) {
        again = true;
        return;
      }
      inFlight = true;
      api
        .readExperiment(experimentId)
        .then((next) => {
          if (!live) return;
          versions.current = versionsOf(next);
          setDetail(next);
          setError(null);
          snapshotApplied = true;
          const waiting = buffered.splice(0);
          if (waiting.some((event) => needsRefetch(versions.current, event))) again = true;
        })
        .catch((caught: unknown) => {
          if (!live) return;
          setError(caught instanceof ApiError ? caught.message : 'Experiment не удалось прочитать.');
          snapshotApplied = true;
        })
        .finally(() => {
          inFlight = false;
          if (live) setLoading(false);
          if (!again || !live) return;
          again = false;
          read();
        });
    };

    const unsubscribe = subscribeToEvents((event) => {
      if (!live) return;
      if (!snapshotApplied) {
        buffered.push(event);
        return;
      }
      // One read settles any number of events: the payload is never on the stream.
      if (needsRefetch(versions.current, event)) read();
    });

    setLoading(true);
    read();

    return () => {
      live = false;
      unsubscribe();
    };
  }, [experimentId, nonce]);

  return { detail, error, loading, refresh, apply };
}
